import { Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { ComponentsModule, FormFieldModule, SnackBarService, SuffixDirective } from '@mastechnicaegroup/carelinelive-ui';
import { AsyncPipe, NgIf } from '@angular/common';
import { AppDatePipe } from '@shared/pipes/date.pipe';
import { PackageDashboardMeta } from '@lib/api/models/package';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Observable, startWith, map, combineLatest, BehaviorSubject, shareReplay, timer } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-dashboard-stats-table',
    imports: [
        SuffixDirective,
        NgIf,
        AppDatePipe,
        ReactiveFormsModule,
        AsyncPipe,
        ComponentsModule,
        FormFieldModule
    ],
    templateUrl: './dashboard-stats-table.component.html',
    standalone: true,
    styleUrl: './dashboard-stats-table.component.scss'
})
export class DashboardStatsTableComponent {
    @Input() title = '';
    @Input() subTitle = '';
    @Input() priority = '';
    @Input()
    set packages(value: PackageDashboardMeta[]) {
        this.emittedPackages.clear();
        this.formattedDates.clear();
        this._packages$.next(value ?? []);
    }

    get packages(): PackageDashboardMeta[] {
        return this._packages$.value;
    }

    @Output() countdownComplete = new EventEmitter<PackageDashboardMeta>();

    private readonly _snackBar = inject(SnackBarService);
    private _destroyRef = inject(DestroyRef);
    private readonly _packages$ = new BehaviorSubject<PackageDashboardMeta[]>([]);
    private readonly appDatePipe = new AppDatePipe();
    private formattedDates: Map<string, string> = new Map<string, string>();
    private emittedPackages = new Set<string>();

    // 15 seconds after the countdown is considered complete
    private static readonly countdownCompleteWindowMs = -15000;


    /** The search form control */
    protected readonly searchControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });

    protected readonly filteredPackages$: Observable<PackageDashboardMeta[]> = combineLatest([
        this.searchControl.valueChanges.pipe(startWith('')),
        this._packages$
    ]).pipe(
        map(([search, packages]) => {
            if (!search?.trim()) {
                return packages;
            }
            const lower = search.toLowerCase().trim();
            return packages.filter(pkg => {
                // check against the formatted dates
                if(pkg.publish_at !== null && !this.formattedDates.has(pkg.publish_at)){
                    this.formattedDates.set(
                        pkg.publish_at,
                        this.appDatePipe.transform(pkg.publish_at, 'dd/MM/yyyy HH:mm')
                    );
                }
                const publishAt = this.formattedDates.get(pkg.publish_at!) ?? '';
                // and check against selected package properties
                return pkg.reference?.toLowerCase().includes(lower) ||
                    pkg.support_required?.toLowerCase().includes(lower) ||
                    pkg.publish?.toLowerCase().includes(lower) ||
                    publishAt.includes(lower) ||
                    pkg.post_code?.toLowerCase().includes(lower)
                }
            );
        }),
        takeUntilDestroyed(this._destroyRef)
    );

    protected readonly timeDifferences$: Observable<Map<string, string>> = combineLatest([
        this.filteredPackages$,
        timer(0, 1000)
    ]).pipe(
        map(([packages]) => {
            const map = new Map<string, string>();
            packages.forEach((pkg: PackageDashboardMeta) => {
                if (pkg.id) {
                    map.set(pkg.id, this.getTimeDifference(pkg));
                }
            });
            return map;
        }),
        // share across multiple template subscribers, tear down when unused
        shareReplay({ bufferSize: 1, refCount: true })
    );

    getTimeDifference(pkg: PackageDashboardMeta): string {
        const timeAt = pkg.broadcast_at??pkg.created_at;
        const timeNow = new Date();
        const timeAtDate = new Date(timeAt);
        const diffMs = timeAtDate.getTime() - timeNow.getTime();

        if(diffMs <= 0) {
            if (
                diffMs <= DashboardStatsTableComponent.countdownCompleteWindowMs &&
                diffMs >= DashboardStatsTableComponent.countdownCompleteWindowMs - 1500 &&
                pkg.id && !this.emittedPackages.has(pkg.id)
            ) {
                this.emittedPackages.add(pkg.id);
                this.countdownComplete.emit(pkg);
            }
            return 'Countdown complete';
        }

        const hours = Math.floor(diffMs / 3600000);
        const minutes = Math.floor((diffMs % 3600000) / 60000);
        const seconds = Math.floor((diffMs % 60000) / 1000);
        return `${hours} hours, ${minutes} mins, ${seconds} seconds`;
    }

    openPackageInNewTab(packageId: string | undefined) {
        if (!packageId) {
            this._snackBar.open({
                colour: 'error',
                icon: 'error_outline',
                title: 'Package not found'
            });
            return;
        }

        const url = `/packages/${packageId}`;
        window.open(url, '_blank');
    }

}
